<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Draggable from 'vuedraggable';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { conversationMatchesSearch as sharedConvMatch } from 'dashboard/helper/conversationSearchHelper';
import ConversationAPI from 'dashboard/api/inbox/conversation';

const props = defineProps({
  show: { type: Boolean, default: false },
  inline: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const store = useStore();
const router = useRouter();
const getters = useStoreGetters();
const accountId = useMapGetter('getCurrentAccountId');
const { isSuperAdmin } = usePermissions();

// Abre la conversación al click en la tarjeta del Kanban (M v4.2 · Victor:
// "al dar click en el lead, debe entrar al chat del cliente"). El modal sigue
// soportando drag & drop para reasignación · este handler solo dispara con
// click simple, no interfiere con el drag.
function openConversation(conv) {
  if (!conv?.id || !accountId.value) return;
  const path = frontendURL(conversationUrl({
    accountId: accountId.value,
    activeInbox: null,
    id: conv.id,
    label: '',
    teamId: 0,
    conversationType: '',
    foldersId: 0,
  }));
  if (path) router.push({ path });
  if (!props.inline) emit('close');
}

const storeConversations = useMapGetter('getAllConversations');
// Req2 · 2026-05-16 (Victor): Leads Asignados cross-branch SCOPEADO.
// El endpoint backend filtra por sucursales accesibles (super=todas,
// admin-suc/GM=las suyas). Mientras carga, fallback al store regular.
const crossConvs = ref(null);
const allConversations = computed(() => crossConvs.value ?? (storeConversations.value || []));
const allAgents = computed(() => getters['agents/getAgents'].value || []);
const branchList = computed(() => store.getters['branches/getBranches'] || []);

const activeBranchFilter = ref('all'); // 'all' | branch.id
const searchQuery = ref('');

async function fetchAssignedLeads() {
  try {
    const { data } = await ConversationAPI.assignedLeadsIndex();
    crossConvs.value = data?.data?.payload || [];
  } catch (e) {
    // Sin permiso (403) o error → mantener fallback al store regular
    crossConvs.value = null;
  }
}

onMounted(() => {
  if (!allAgents.value.length) store.dispatch('agents/get');
  if (!branchList.value.length) store.dispatch('branches/get');
  fetchAssignedLeads();
});

function close() { emit('close'); }

function branchClass(branch) {
  if (!branch) return 'none';
  if (branch.is_personal) return 'pers';
  const code = (branch.code || '').toLowerCase();
  if (['lrm', 'sde', 'sdo', 'stg'].includes(code)) return code;
  return 'other';
}

function isAgentInBranch(agent, branchId) {
  if (branchId === 'all') return true;
  const ids = agent.accessible_branch_ids || [];
  if (agent.role === 'administrator' && ids.length === 0) return true;
  return ids.includes(branchId);
}

// Buscador inteligente · usa helper compartido (normaliza teléfono, multi-campo)
function conversationMatchesSearch(conv, q) {
  return sharedConvMatch(conv, q);
}

function agentMatchesSearch(agent, q) {
  if (!q) return true;
  return (agent.name || '').toLowerCase().includes(q) ||
         (agent.email || '').toLowerCase().includes(q);
}

const visibleAgents = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return allAgents.value.filter(a => {
    if (!isAgentInBranch(a, activeBranchFilter.value)) return false;
    if (!q) return true;
    if (agentMatchesSearch(a, q)) return true;
    // O alguno de sus chats matchea
    const chats = (allConversations.value || []).filter(c => c.meta?.assignee?.id === a.id);
    return chats.some(c => conversationMatchesSearch(c, q));
  });
});

function unassignedConversations() {
  const q = searchQuery.value.trim().toLowerCase();
  let chats = (allConversations.value || []).filter(c => !c.meta?.assignee?.id);
  if (q) chats = chats.filter(c => conversationMatchesSearch(c, q));
  return chats;
}

function conversationsForAgent(agentId) {
  const q = searchQuery.value.trim().toLowerCase();
  let chats = (allConversations.value || []).filter(c => c.meta?.assignee?.id === agentId);
  if (q) {
    const agent = allAgents.value.find(a => a.id === agentId);
    // Si el agente matchea por nombre/email, mostrar TODAS sus conversaciones
    // Si NO, mostrar solo las conversaciones que matcheen el query
    if (!agent || !agentMatchesSearch(agent, q)) {
      chats = chats.filter(c => conversationMatchesSearch(c, q));
    }
  }
  return chats;
}

function getAgentInitials(name) {
  if (!name) return '??';
  return name.split(' ').slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
}

function getAgentBranchClass(agent) {
  const ids = agent.accessible_branch_ids || [];
  // super admin (administrator + lista vacía) → "stg" como dorado fallback
  if (agent.role === 'administrator' && ids.length === 0) return 'stg';
  if (!ids.length) return 'other';
  const branch = branchList.value.find(b => b.id === ids[0]);
  return branchClass(branch);
}

function getAgentPrimaryBranch(agent) {
  const ids = agent.accessible_branch_ids || [];
  if (agent.role === 'administrator' && ids.length === 0) return { code: 'TODAS', name: 'Todas' };
  if (!ids.length) return null;
  return branchList.value.find(b => b.id === ids[0]);
}

function rolePill(agent) {
  if (agent.role === 'administrator') {
    if ((agent.accessible_branch_ids || []).length === 0) return { label: '⭐ SUPER', cls: 'super' };
    return { label: '🛡 ADMIN', cls: 'admin' };
  }
  return { label: '👤 AGENTE', cls: 'agent' };
}

function avatarColor(agent) {
  const colors = [
    'linear-gradient(135deg, #fbbf24, #d97706)',
    'linear-gradient(135deg, #f472b6, #be185d)',
    'linear-gradient(135deg, #818cf8, #6366f1)',
    'linear-gradient(135deg, #34d399, #059669)',
    'linear-gradient(135deg, #60a5fa, #1d4ed8)',
    'linear-gradient(135deg, #c084fc, #7c3aed)',
  ];
  return colors[agent.id % colors.length];
}

function senderInitials(conversation) {
  const name = conversation.meta?.sender?.name || '';
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
}

function senderName(conversation) {
  return conversation.meta?.sender?.name || `Lead #${conversation.id}`;
}

function lastPreview(conversation) {
  const msg = getLastMessage(conversation);
  if (!msg) return '';
  if (msg.content) return msg.content.substring(0, 80);
  if (msg.attachments?.length) return '📎 Archivo adjunto';
  return '';
}

function conversationBranchClass(c) {
  const branchId = c.branch_id;
  if (!branchId) return 'none';
  const branch = branchList.value.find(b => b.id === branchId);
  return branchClass(branch);
}

function conversationBranchCode(c) {
  const branch = branchList.value.find(b => b.id === c.branch_id);
  if (!branch) return '';
  return (branch.code || branch.name?.substring(0, 3) || '').toUpperCase();
}

function timeAgo(timestamp) {
  if (!timestamp) return '';
  const seconds = Math.floor(Date.now() / 1000 - timestamp);
  if (seconds < 60) return 'ahora';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

const stats = computed(() => {
  const total = allConversations.value?.length || 0;
  const unassigned = unassignedConversations().length;
  const activeAgents = visibleAgents.value.filter(a => conversationsForAgent(a.id).length > 0).length;
  return { total, unassigned, activeAgents };
});

// Drag & drop · reasignar conversación
const saving = ref({});

async function reassignConversation(conversation, newAgent) {
  if (!conversation || !newAgent) return;
  if (conversation.meta?.assignee?.id === newAgent.id) return;
  saving.value[conversation.id] = true;
  try {
    await store.dispatch('assignAgent', {
      conversationId: conversation.id,
      agentId: newAgent.id,
    });
    useAlert(`✓ Asignado a ${newAgent.name}`);
  } catch (e) {
    useAlert('Error al reasignar');
  } finally {
    saving.value[conversation.id] = false;
  }
}

async function unassignConversation(conversation) {
  if (!conversation) return;
  saving.value[conversation.id] = true;
  try {
    await store.dispatch('assignAgent', {
      conversationId: conversation.id,
      agentId: null,
    });
    useAlert('✓ Sin asignar');
  } catch (e) {
    useAlert('Error al desasignar');
  } finally {
    saving.value[conversation.id] = false;
  }
}

function onAddedToAgent(evt, agent) {
  if (!evt.added) return;
  reassignConversation(evt.added.element, agent);
}

function onAddedToUnassigned(evt) {
  if (!evt.added) return;
  unassignConversation(evt.added.element);
}

// ── Navegación del tablero (Victor 2026-05-15): rueda del mouse para
// moverse izq/der + arriba/abajo, y clic-arrastrar en zona vacía para
// "agarrar" el tablero. Funciona con trackpad de laptop y mouse, Win/Mac.
const kanbanWrapRef = ref(null);

function onKanbanWheel(e) {
  const wrap = kanbanWrapRef.value;
  if (!wrap) return;
  // Trackpad con gesto horizontal → dejar scroll nativo (ya lo maneja overflow-x).
  if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
  // Si el cursor está sobre una lista de agente que aún puede scrollear
  // vertical en esa dirección, respetar el scroll vertical nativo.
  const body = e.target.closest?.('.ak-col-body');
  if (body) {
    const canDown = e.deltaY > 0 && body.scrollTop + body.clientHeight < body.scrollHeight - 1;
    const canUp = e.deltaY < 0 && body.scrollTop > 0;
    if (canDown || canUp) return;
  }
  // Rueda vertical de un mouse normal → desplazamiento horizontal del tablero.
  e.preventDefault();
  wrap.scrollLeft += e.deltaY;
}

const isPanning = ref(false);
let panStartX = 0;
let panStartY = 0;
let panScrollLeft = 0;
let panBody = null;
let panBodyTop = 0;

function onKanbanPanStart(e) {
  if (e.button !== 0) return;
  // No iniciar pan sobre tarjetas ni elementos interactivos (no romper
  // el drag & drop de vuedraggable ni los clics en las tarjetas).
  if (e.target.closest('.ak-conv-card, button, a, input, [draggable="true"]')) return;
  const wrap = kanbanWrapRef.value;
  if (!wrap) return;
  isPanning.value = true;
  panStartX = e.clientX;
  panStartY = e.clientY;
  panScrollLeft = wrap.scrollLeft;
  panBody = e.target.closest('.ak-col-body');
  panBodyTop = panBody ? panBody.scrollTop : 0;
  window.addEventListener('mousemove', onKanbanPanMove);
  window.addEventListener('mouseup', onKanbanPanEnd);
}

function onKanbanPanMove(e) {
  if (!isPanning.value) return;
  const wrap = kanbanWrapRef.value;
  if (!wrap) return;
  wrap.scrollLeft = panScrollLeft - (e.clientX - panStartX);
  if (panBody) panBody.scrollTop = panBodyTop - (e.clientY - panStartY);
}

function onKanbanPanEnd() {
  isPanning.value = false;
  panBody = null;
  window.removeEventListener('mousemove', onKanbanPanMove);
  window.removeEventListener('mouseup', onKanbanPanEnd);
}
</script>

<template>
  <div v-if="(show || inline) && isSuperAdmin" :class="inline ? 'ak-inline-wrap' : 'ak-overlay'" @click.self="!inline && close()">
    <div :class="inline ? 'ak-modal ak-modal-inline' : 'ak-modal'">
      <div class="ak-head">
        <div class="ak-head-icon">👥</div>
        <div>
          <div class="ak-head-title">Conversaciones por agente</div>
          <div class="ak-head-sub">Vista Kanban · drag &amp; drop para reasignar</div>
        </div>
        <div class="ak-head-stats">
          <div class="ak-stat-pill"><span class="ak-num">{{ stats.total }}</span> conversaciones</div>
          <div class="ak-stat-pill ak-warn"><span style="color:#fcd34d">⚠</span> <span class="ak-num">{{ stats.unassigned }}</span> sin asignar</div>
          <div class="ak-stat-pill"><span class="ak-num">{{ stats.activeAgents }}</span> agentes activos</div>
        </div>
        <div class="ak-search">
          <span style="color: rgba(255,255,255,0.45)">🔍</span>
          <input v-model="searchQuery" placeholder="Buscar agente o cliente…" />
        </div>
        <button v-if="!inline" class="ak-x" @click="close">×</button>
      </div>

      <div class="ak-toolbar">
        <div class="ak-toolbar-label">SUCURSAL</div>
        <div
          class="ak-filter-chip"
          :class="{ 'ak-filter-active': activeBranchFilter === 'all' }"
          @click="activeBranchFilter = 'all'"
        >
          <div class="ak-filter-dot" style="background: linear-gradient(135deg, #fbbf24, #d97706)"></div>
          Todas
        </div>
        <div
          v-for="branch in branchList"
          :key="`bf-${branch.id}`"
          class="ak-filter-chip"
          :class="{ 'ak-filter-active': activeBranchFilter === branch.id }"
          @click="activeBranchFilter = branch.id"
        >
          <div class="ak-filter-dot" :class="branchClass(branch)"></div>
          {{ (branch.code || branch.name?.substring(0, 4) || '').toUpperCase() }}
        </div>
      </div>

      <div
        ref="kanbanWrapRef"
        class="ak-kanban-wrap"
        :class="{ 'ak-panning': isPanning }"
        @wheel="onKanbanWheel"
        @mousedown="onKanbanPanStart"
      >
        <div class="ak-kanban">
          <!-- Sin asignar (ocultar si hay búsqueda y no matchea ninguno) -->
          <div
            v-if="!searchQuery.trim() || unassignedConversations().length > 0"
            class="ak-col ak-col-unassigned"
          >
            <div class="ak-col-head">
              <div class="ak-col-av ak-col-av-unassigned">?</div>
              <div class="ak-col-info">
                <div class="ak-col-name">SIN ASIGNAR</div>
                <div class="ak-col-meta">⚠ Necesitan agente</div>
              </div>
              <div class="ak-col-count ak-col-count-unassigned">{{ unassignedConversations().length }}</div>
            </div>
            <Draggable
              :model-value="unassignedConversations()"
              :group="{ name: 'conv', pull: true, put: true }"
              item-key="id"
              class="ak-col-body"
              ghost-class="ak-card-ghost"
              :animation="180"
              @change="onAddedToUnassigned"
            >
              <template #item="{ element: conv }">
                <div class="ak-conv-card" :class="{ 'ak-conv-unread': conv.unread_count > 0 }" @click="openConversation(conv)">
                  <div class="ak-cc-row1">
                    <div class="ak-cc-av">{{ senderInitials(conv) }}</div>
                    <div class="ak-cc-name" :class="{ 'ak-cc-name-unread': conv.unread_count > 0 }">{{ senderName(conv) }}</div>
                    <div v-if="conversationBranchCode(conv)" class="ak-cc-branch" :class="conversationBranchClass(conv)">{{ conversationBranchCode(conv) }}</div>
                  </div>
                  <div v-if="lastPreview(conv)" class="ak-cc-preview">{{ lastPreview(conv) }}</div>
                  <div class="ak-cc-foot">
                    <span class="ak-cc-time">⏱ {{ timeAgo(conv.last_activity_at) }}</span>
                    <span v-if="conv.unread_count > 0" class="ak-cc-unread-pill">{{ conv.unread_count > 9 ? '9+' : conv.unread_count }}</span>
                  </div>
                </div>
              </template>
            </Draggable>
          </div>

          <!-- Una columna por agente -->
          <div
            v-for="agent in visibleAgents"
            :key="`agent-${agent.id}`"
            class="ak-col"
            :class="`ak-col-${getAgentBranchClass(agent)}`"
          >
            <div class="ak-col-head">
              <div class="ak-col-av" :style="{ background: avatarColor(agent) }">{{ getAgentInitials(agent.name) }}</div>
              <div class="ak-col-info">
                <div class="ak-col-name">{{ agent.name }}</div>
                <div class="ak-col-meta">
                  <span class="ak-role-mini" :class="rolePill(agent).cls">{{ rolePill(agent).label }}</span>
                  <span v-if="getAgentPrimaryBranch(agent)" class="ak-col-branch-mini" :class="getAgentBranchClass(agent)">
                    {{ (getAgentPrimaryBranch(agent).code || getAgentPrimaryBranch(agent).name?.substring(0, 3) || '').toUpperCase() }}
                  </span>
                </div>
              </div>
              <div class="ak-col-count" :class="`count-${getAgentBranchClass(agent)}`">{{ conversationsForAgent(agent.id).length }}</div>
            </div>
            <Draggable
              :model-value="conversationsForAgent(agent.id)"
              :group="{ name: 'conv', pull: true, put: true }"
              item-key="id"
              class="ak-col-body"
              ghost-class="ak-card-ghost"
              :animation="180"
              @change="(evt) => onAddedToAgent(evt, agent)"
            >
              <template #item="{ element: conv }">
                <div class="ak-conv-card" :class="{ 'ak-conv-unread': conv.unread_count > 0 }" @click="openConversation(conv)">
                  <div class="ak-cc-row1">
                    <div class="ak-cc-av">{{ senderInitials(conv) }}</div>
                    <div class="ak-cc-name" :class="{ 'ak-cc-name-unread': conv.unread_count > 0 }">{{ senderName(conv) }}</div>
                    <div v-if="conversationBranchCode(conv)" class="ak-cc-branch" :class="conversationBranchClass(conv)">{{ conversationBranchCode(conv) }}</div>
                  </div>
                  <div v-if="lastPreview(conv)" class="ak-cc-preview">{{ lastPreview(conv) }}</div>
                  <div class="ak-cc-foot">
                    <span class="ak-cc-time">⏱ {{ timeAgo(conv.last_activity_at) }}</span>
                    <span v-if="conv.unread_count > 0" class="ak-cc-unread-pill">{{ conv.unread_count > 9 ? '9+' : conv.unread_count }}</span>
                  </div>
                </div>
              </template>
            </Draggable>
          </div>
        </div>
      </div>

      <div class="ak-foot">
        <span>💡 <strong>Tip:</strong> arrastra una conversación a otro agente para reasignar</span>
        <span style="margin-left: auto">{{ visibleAgents.length }} agentes mostrados</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ak-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.65);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, system-ui, sans-serif;
  color: #fff;
}

/* Modo inline · ocupa el espacio del contenedor padre */
.ak-inline-wrap {
  width: 100%;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: -apple-system, system-ui, sans-serif;
  color: #fff;
  background: #050505;
}

.ak-modal {
  position: fixed; inset: 16px 32px 32px 32px;
  background: linear-gradient(180deg, rgba(15,15,18,0.98), rgba(8,8,10,0.98));
  border: 1px solid rgba(251,191,36,0.20);
  border-radius: 18px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.70), 0 0 0 1px rgba(251,191,36,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ak-modal-inline {
  position: relative;
  inset: auto;
  width: 100%;
  height: 100%;
  min-width: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: #050505;
}
.ak-modal::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(251,191,36,0.40), transparent);
}

.ak-head {
  padding: 18px 26px;
  display: flex;
  align-items: center;
  gap: 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  background: linear-gradient(135deg, rgba(251,191,36,0.06), transparent 60%);
  flex-shrink: 0;
}
.ak-head-icon {
  width: 42px; height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.05));
  border: 1px solid rgba(251,191,36,0.45);
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; color: #fbbf24;
}
.ak-head-title { font-size: 18px; font-weight: 900; }
.ak-head-sub { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 2px; }

.ak-head-stats { margin-left: 24px; display: flex; gap: 10px; }
.ak-stat-pill {
  padding: 7px 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 999px;
  font-size: 11px;
  color: rgba(255,255,255,0.65);
  display: flex; align-items: center; gap: 6px;
}
.ak-num { font-weight: 900; color: #fff; }
.ak-stat-pill.ak-warn {
  border-color: rgba(245,158,11,0.45);
  color: #fcd34d;
  background: rgba(245,158,11,0.06);
}
.ak-stat-pill.ak-warn .ak-num { color: #fbbf24; }

.ak-search {
  margin-left: auto;
  padding: 6px 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 999px;
  display: flex; align-items: center; gap: 8px;
  min-width: 240px;
}
.ak-search input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 11.5px;
  font-family: inherit;
}

.ak-x {
  width: 32px; height: 32px;
  border-radius: 10px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.50);
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  cursor: pointer;
  font-family: inherit;
}
.ak-x:hover {
  background: rgba(239,68,68,0.10);
  color: #f87171;
  border-color: rgba(239,68,68,0.30);
}

.ak-toolbar {
  padding: 12px 26px;
  display: flex;
  gap: 8px;
  align-items: center;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  flex-shrink: 0;
  flex-wrap: wrap;
}
.ak-toolbar-label {
  font-size: 10.5px;
  font-weight: 800;
  color: rgba(255,255,255,0.50);
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-right: 4px;
}
.ak-filter-chip {
  padding: 5px 11px 5px 8px;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(255,255,255,0.02);
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  color: rgba(255,255,255,0.65);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.18s;
}
.ak-filter-chip:hover { background: rgba(255,255,255,0.05); }
.ak-filter-active {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.45);
  color: #fff;
}
.ak-filter-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
}
.ak-filter-dot.lrm  { background: #a855f7; }
.ak-filter-dot.sde  { background: #22c55e; }
.ak-filter-dot.sdo  { background: #3b82f6; }
.ak-filter-dot.stg  { background: #f59e0b; }
.ak-filter-dot.pers { background: #6366f1; }
.ak-filter-dot.other { background: #94a3b8; }

.ak-kanban-wrap {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 18px 26px;
  cursor: grab;
  scroll-behavior: auto;
}
.ak-kanban-wrap.ak-panning {
  cursor: grabbing;
  user-select: none;
}
.ak-kanban-wrap.ak-panning .ak-conv-card {
  pointer-events: none;
}
.ak-kanban {
  display: flex;
  gap: 14px;
  height: 100%;
  min-width: 100%;
}

.ak-col {
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
  background: rgba(15,15,18,0.50);
  border: 1.5px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  overflow: hidden;
  max-height: 100%;
}
.ak-col-lrm { border-color: rgba(168,85,247,0.45); }
.ak-col-sde { border-color: rgba(34,197,94,0.45); }
.ak-col-sdo { border-color: rgba(59,130,246,0.45); }
.ak-col-stg { border-color: rgba(245,158,11,0.45); }
.ak-col-pers { border-color: rgba(99,102,241,0.45); }
.ak-col-other { border-color: rgba(255,255,255,0.20); }
.ak-col-unassigned { border-color: rgba(239,68,68,0.45); }

.ak-col-head {
  padding: 14px 14px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  display: flex;
  align-items: center;
  gap: 10px;
}
.ak-col-lrm .ak-col-head { background: linear-gradient(135deg, rgba(168,85,247,0.12), transparent); }
.ak-col-sde .ak-col-head { background: linear-gradient(135deg, rgba(34,197,94,0.12), transparent); }
.ak-col-sdo .ak-col-head { background: linear-gradient(135deg, rgba(59,130,246,0.12), transparent); }
.ak-col-stg .ak-col-head { background: linear-gradient(135deg, rgba(245,158,11,0.12), transparent); }
.ak-col-pers .ak-col-head { background: linear-gradient(135deg, rgba(99,102,241,0.12), transparent); }
.ak-col-unassigned .ak-col-head { background: linear-gradient(135deg, rgba(239,68,68,0.10), transparent); }

.ak-col-av {
  width: 36px; height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 12px;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(0,0,0,0.30);
}
.ak-col-av-unassigned {
  background: rgba(239,68,68,0.20);
  color: #f87171;
  border: 1.5px dashed rgba(239,68,68,0.45);
}
.ak-col-info { flex: 1; min-width: 0; }
.ak-col-name {
  font-size: 13px;
  font-weight: 900;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ak-col-meta {
  font-size: 9.5px;
  color: rgba(255,255,255,0.55);
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.ak-role-mini {
  font-size: 8px;
  padding: 1px 4px;
  border-radius: 3px;
  font-weight: 800;
}
.ak-role-mini.super { background: rgba(251,191,36,0.18); color: #fbbf24; }
.ak-role-mini.admin { background: rgba(239,68,68,0.18); color: #f87171; }
.ak-role-mini.agent { background: rgba(59,130,246,0.18); color: #60a5fa; }

.ak-col-branch-mini {
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 800;
}
.ak-col-branch-mini.lrm  { background: rgba(168,85,247,0.20); color: #c4b5fd; }
.ak-col-branch-mini.sde  { background: rgba(34,197,94,0.20); color: #6ee7b7; }
.ak-col-branch-mini.sdo  { background: rgba(59,130,246,0.20); color: #93c5fd; }
.ak-col-branch-mini.stg  { background: rgba(245,158,11,0.20); color: #fcd34d; }
.ak-col-branch-mini.pers { background: rgba(99,102,241,0.20); color: #a5b4fc; }

.ak-col-count {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 900;
  flex-shrink: 0;
}
.ak-col-count.count-lrm  { background: rgba(168,85,247,0.20); color: #c4b5fd; border: 1px solid rgba(168,85,247,0.45); }
.ak-col-count.count-sde  { background: rgba(34,197,94,0.20); color: #6ee7b7; border: 1px solid rgba(34,197,94,0.45); }
.ak-col-count.count-sdo  { background: rgba(59,130,246,0.20); color: #93c5fd; border: 1px solid rgba(59,130,246,0.45); }
.ak-col-count.count-stg  { background: rgba(245,158,11,0.20); color: #fcd34d; border: 1px solid rgba(245,158,11,0.45); }
.ak-col-count.count-pers { background: rgba(99,102,241,0.20); color: #a5b4fc; border: 1px solid rgba(99,102,241,0.45); }
.ak-col-count.count-other { background: rgba(255,255,255,0.10); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.20); }
.ak-col-count-unassigned {
  background: rgba(239,68,68,0.20);
  color: #f87171;
  border: 1px solid rgba(239,68,68,0.45);
}

.ak-col-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 80px;
}

.ak-conv-card {
  padding: 10px 12px;
  background: rgba(15,15,18,0.92);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 9px;
  cursor: grab;
  transition: all 0.18s;
}
.ak-conv-card:hover {
  border-color: rgba(251,191,36,0.40);
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(0,0,0,0.40);
}
.ak-conv-card:active { cursor: grabbing; }
.ak-conv-unread {
  border-color: rgba(251,191,36,0.30);
  background: linear-gradient(180deg, rgba(251,191,36,0.04), rgba(8,8,10,0.92));
}
.ak-card-ghost { opacity: 0.4; }

.ak-cc-row1 {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 4px;
}
.ak-cc-av {
  width: 26px; height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #818cf8, #6366f1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 10px;
  color: #fff;
  flex-shrink: 0;
}
.ak-cc-name {
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ak-cc-name-unread { color: #fbbf24; }

.ak-cc-branch {
  font-size: 8px;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 3px;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}
.ak-cc-branch.lrm  { background: rgba(168,85,247,0.20); color: #c4b5fd; }
.ak-cc-branch.sde  { background: rgba(34,197,94,0.20); color: #6ee7b7; }
.ak-cc-branch.sdo  { background: rgba(59,130,246,0.20); color: #93c5fd; }
.ak-cc-branch.stg  { background: rgba(245,158,11,0.20); color: #fcd34d; }
.ak-cc-branch.pers { background: rgba(99,102,241,0.20); color: #a5b4fc; }
.ak-cc-branch.other { background: rgba(255,255,255,0.10); color: rgba(255,255,255,0.60); }

.ak-cc-preview {
  font-size: 11px;
  color: rgba(255,255,255,0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  padding-left: 33px;
  margin-top: -2px;
}
.ak-cc-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.06);
  padding-left: 33px;
  font-size: 9.5px;
  color: rgba(255,255,255,0.40);
}
.ak-cc-unread-pill {
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #18181b;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 900;
}

.ak-foot {
  padding: 12px 26px;
  border-top: 1px solid rgba(255,255,255,0.06);
  background: rgba(0,0,0,0.40);
  font-size: 10.5px;
  color: rgba(255,255,255,0.45);
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}
.ak-foot strong { color: #fbbf24; }
</style>
